import { trpc } from '@web/lib/trpc';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Paperclip,
  Send,
  X,
  Mail,
  MessageSquare,
  MessageCircle,
  MessagesSquare,
  Share2,
  ChevronDown,
  LoaderIcon,
} from 'lucide-react';

import { Attach } from '@web/components/ui/attach';
import { Badge } from '@web/components/ui/badge';
import { Button } from '@web/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@web/components/ui/dropdown-menu';

import {
  AutosizeTextAreaRef,
  TextareaAutosize,
} from '@web/components/ui/text-area-autosize';

import { Spinner } from '@web/components/ui/spinner';
import { Media } from '@web/components/ui/media';

import { wait } from '@shared/utils';
import {
  CommunicationChannelEnum,
  communicationChannelEnum,
} from '@shared/enums';
import { cn } from '@web/lib/shadcn';

export function MessageInput({
  activeContactId,
  initText,
  onSend,
}: {
  activeContactId?: string;
  initText?: string;
  onSend?: () => void;
}) {
  const { mutateAsync: sendMessageAsync } =
    trpc.messaging.sendMessage.useMutation();

  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [selectedChannel, setSelectedChannel] =
    useState<CommunicationChannelEnum>('sms');

  // Map channel types to their respective icons
  const channelIcons: Record<CommunicationChannelEnum, React.ReactNode> = {
    sms: <MessageSquare className="h-4 w-4" />,
    email: <Mail className="h-4 w-4" />,
    whatsapp: <MessagesSquare className="h-4 w-4" />,
    slack: <MessageCircle className="h-4 w-4" />,
    instagram: <Share2 className="h-4 w-4" />,
  };

  // Map channel types to their respective tooltips
  const channelTooltips: Record<CommunicationChannelEnum, string> = {
    sms: 'SMS',
    email: 'Email',
    whatsapp: 'WhatsApp',
    slack: 'Slack',
    instagram: 'Instagram',
  };

  const sendMessage = useCallback(
    async (message: string, attachmentFiles?: File[]) => {
      if (activeContactId !== undefined) {
        const attachments = await Promise.all(
          attachmentFiles?.map(async (file) => {
            const base64 = await fileToBase64(file);
            return {
              content: base64.split(',')[1], // Remove the data URL prefix
              type: file.type,
            };
          }) ?? [],
        );

        await sendMessageAsync({
          contactId: activeContactId,
          message,
          attachments,
          channelType: selectedChannel,
        });
      }
    },
    [sendMessageAsync, activeContactId, selectedChannel],
  );

  const removeAttachment = useCallback((name: string) => {
    setAttachments((prev) => {
      return prev.filter((f) => f.name !== name);
    });
  }, []);

  const [attachmentsFileContents, setAttachmentsFileContents] = useState<
    {
      name: string;
      type: string;
      content: string;
    }[]
  >([]);

  useEffect(() => {
    const updateAttachmentsFileContents = async () => {
      const filesContents = await Promise.all(
        attachments.map(async (file) => {
          const base64 = await fileToBase64(file);
          return {
            name: file.name,
            content: base64.split(',')[1], // Remove the data URL prefix
            type: file.type,
          };
        }),
      );

      setAttachmentsFileContents(filesContents);
    };

    void updateAttachmentsFileContents();
  }, [attachments]);

  useEffect(() => {
    if (initText) {
      setNewMessage((prev) => prev + initText);
    }
  }, [initText]);

  const numAttachments = attachments.length;
  const canSend = newMessage.trim() !== '' || numAttachments > 0;

  const textAreaRef = useRef<AutosizeTextAreaRef | null>(null);

  const handleSendMessage = useCallback(async () => {
    if (canSend) {
      setIsSending(true);
      await sendMessage(newMessage, attachments);
      setIsSending(false);

      setNewMessage('');
      setAttachments([]);

      onSend?.();

      await wait(1);
      textAreaRef.current?.textArea.focus();
    }
  }, [newMessage, sendMessage, onSend, attachments, canSend]);

  const handleNewMessageChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNewMessage(event.target.value);
    },
    [],
  );

  const handleKeyPress = useCallback(
    async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        await handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  return (
    <>
      <div className="flex items-center border-t bg-white p-2 px-4">
        <div className="relative">
          <Attach
            className="relative overflow-visible"
            setAttachment={setAttachments}
            disabled={isSending}
          >
            <Button
              size="sm"
              variant="ghost"
              className="h-9 w-9 rounded-full p-0 hover:bg-gray-100"
            >
              <Paperclip className="h-4 w-4 text-gray-500" />
            </Button>
          </Attach>

          {numAttachments > 0 && (
            <>
              <Badge
                variant="default"
                className="absolute -right-0.5 -top-0.5 flex size-4 cursor-default items-center justify-center rounded-full px-0 py-0 text-[10px]"
              >
                {numAttachments > 9 ? '9+' : numAttachments}
              </Badge>

              <Button
                variant="destructive"
                size="icon"
                className="absolute left-0 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 p-0 shadow-sm"
                onClick={() => {
                  setAttachments([]);
                }}
              >
                <X className="size-2.5" />
              </Button>
            </>
          )}
        </div>

        <TextareaAutosize
          ref={textAreaRef}
          placeholder="Type a message..."
          className="mx-2 flex-1 rounded-2xl border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:ring-0 focus:ring-offset-0"
          maxHeight={200}
          value={newMessage}
          onChange={handleNewMessageChange}
          onKeyDown={(e) => {
            void handleKeyPress(e);
          }}
          disabled={isSending}
        />

        <div className="flex space-x-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 rounded-full p-0 hover:bg-gray-100"
                disabled={isSending}
              >
                <div className="flex items-center justify-center">
                  {channelIcons[selectedChannel]}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-32">
              {communicationChannelEnum.options.map((channel) => (
                <DropdownMenuItem
                  key={channel}
                  onClick={() => {
                    setSelectedChannel(channel);
                  }}
                  className="flex items-center gap-2 py-1.5"
                >
                  {channelIcons[channel]}
                  <span className="text-xs font-medium">
                    {channelTooltips[channel]}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="sm"
            variant={canSend ? 'default' : 'ghost'}
            className={cn(
              'h-9 w-9 rounded-full p-0',
              canSend ? 'bg-primary hover:bg-primary/90' : 'hover:bg-gray-100',
            )}
            aria-label="Send message"
            onClick={() => {
              void handleSendMessage();
            }}
            disabled={!canSend || isSending}
          >
            {isSending ? (
              <LoaderIcon className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {attachmentsFileContents.length > 0 && (
        <div className="flex gap-2 border-t bg-gray-50 p-2 px-4">
          {attachmentsFileContents.map((file, idx) => (
            <div key={idx} className="group relative">
              <Thumbnail file={file} />

              <Button
                variant="destructive"
                size="icon"
                className="absolute -right-1 -top-1 h-4 w-4 rounded-full p-0 opacity-90 shadow group-hover:opacity-100"
                onClick={() => {
                  removeAttachment(file.name);
                }}
              >
                <X className="h-2 w-2" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Thumbnail({ file }: { file: { type: string; content: string } }) {
  return (
    <Media file={file} className="size-12 rounded-md object-cover shadow-sm" />
  );
}

/**
 * Converts a File object to a Base64 string using FileReader.
 * @param file The file to convert.
 * @returns A promise that resolves to the Base64 string.
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not a string.'));
      }
    };
    reader.onerror = (error) => {
      reject(new Error('Error reading file', { cause: error }));
    };
  });
}
